
import { getGlobalCookie } from "../../utils";
import { globalGetAiService, globalPostAiService, globalPutAiService } from "../../utils/globalApiServices";
import { AI_CONSENT_POLICY_VERSIONS } from "../consentConstants";

const lessorId = (() => {
  try {
    return JSON.parse(getGlobalCookie("lessorAccess"));
  } catch {
    return null;
  }
})();

export const UNAUTHORIZED_EVENT = "lease-ai:unauthorized";

const createTitle = (question) => {
  const trimmed = question.trim().replace(/\s+/g, " ");
  return trimmed || "New chat";
};

const getApiError = (error) => {
  const status = error?.response?.status ?? error?.status ?? error?.data?.status;
  const data = error?.response?.data ?? error?.data;
  const message =
    data?.message ||
    data?.detail ||
    (typeof data?.error === "string" ? data.error : null) ||
    error?.message ||
    "Something went wrong. Please try again.";
  const normalized = new Error(message);
  normalized.status = status != null ? Number(status) : undefined;
  normalized.data = data;
  // Axios sets code ERR_NETWORK when the response is unreadable (e.g. CORS-blocked 403).
  if (error?.code) {
    normalized.code = error.code;
  }
  // Preserve axios response for callers that still read error.response
  if (error?.response) {
    normalized.response = error.response;
  }
  return normalized;
};

/**
 * Opt-in local error simulator for UI testing.
 * Enable from browser console, then trigger the matching API action:
 *
 *   localStorage.setItem("AI_FORCE_ERROR", JSON.stringify({
 *     status: 400,
 *     message: "Invalid question format",
 *     match: "/questions"   // optional path fragment; omit to affect all AI calls
 *   }))
 *
 * Clear with:
 *   localStorage.removeItem("AI_FORCE_ERROR")
 */
const getForcedApiError = (path) => {
  try {
    const raw = localStorage.getItem("AI_FORCE_ERROR");
    if (!raw) return null;
    const config = JSON.parse(raw);
    if (config?.match && !String(path).includes(String(config.match))) {
      return null;
    }
    return getApiError({
      response: {
        status: Number(config.status) || 500,
        data: {
          message: config.message || "Simulated API error",
          ...(config.data && typeof config.data === "object" ? config.data : {})
        }
      }
    });
  } catch {
    return null;
  }
};

const apiFetch = async (path, options = {}) => {
  const method = (options.method || "GET").toUpperCase();
  const body = options.body ? JSON.parse(options.body) : undefined;

  const forcedError = getForcedApiError(path);
  if (forcedError) throw forcedError;

  try {
    let response;
    if (method === "GET") {
      response = await globalGetAiService(`${path}`);
    } else if (method === "PUT") {
      response = await globalPutAiService(`${path}`, body);
    } else {
      response = await globalPostAiService(`${path}`, body);
    }

    const data = response.data;
    return data && typeof data === "object" && "data" in data ? data.data : data;
  } catch (error) {
    throw getApiError(error);
  }
};

const normalizeAllUsersTokenRequests = (response, page = 1, pageSize = 20) => {
  if (Array.isArray(response)) {
    const total = response.length;
    return {
      list: response,
      pagination: {
        page,
        per_page: pageSize,
        total,
        total_pages: Math.max(1, Math.ceil(total / pageSize) || 1)
      }
    };
  }

  if (response?.list && response?.pagination) {
    const perPage = Number(response.pagination.per_page || pageSize);
    const total = Number(response.pagination.total || response.list.length || 0);
    const totalPages = Math.max(
      1,
      Number(response.pagination.total_pages) || Math.ceil(total / perPage) || 1
    );
    return {
      list: response.list || [],
      pagination: {
        page: Number(response.pagination.page || page),
        per_page: perPage,
        total,
        total_pages: totalPages
      }
    };
  }

  const results = response?.results || response?.data || [];
  const list = Array.isArray(results) ? results : [];
  const perPage = Number(response?.page_size || response?.per_page || pageSize);
  const total = Number(response?.count || response?.total || list.length);
  const totalPages = Math.max(
    1,
    Number(response?.total_pages) || Math.ceil(total / perPage) || 1
  );
  return {
    list,
    pagination: {
      page: Number(response?.page || page),
      per_page: perPage,
      total,
      total_pages: totalPages
    }
  };
};

const paginateList = (items, currentPage, pageSize) => {
  const perPage = Number(pageSize) || 10;
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage) || 1);
  const safePage = Math.min(Math.max(1, Number(currentPage) || 1), totalPages);
  const start = (safePage - 1) * perPage;
  return {
    list: items.slice(start, start + perPage),
    pagination: {
      page: safePage,
      per_page: perPage,
      total,
      total_pages: totalPages
    }
  };
};

export const MockApi = {
  // GET /api/ai/consent — current acceptance status for the authenticated user
  async loadConsentStatus() {
    return apiFetch("/api/ai/consent");
  },

  // GET /api/ai/consent/policy — latest policy versions for acceptance payload
  async loadConsentPolicy() {
    return apiFetch("/api/ai/consent/policy");
  },

  // POST /api/ai/consent — record acceptance with policy versions
  async acceptConsent({
    terms_version = AI_CONSENT_POLICY_VERSIONS.terms_version,
    data_policy_version = AI_CONSENT_POLICY_VERSIONS.data_policy_version,
    privacy_policy_version = AI_CONSENT_POLICY_VERSIONS.privacy_policy_version
  } = {}) {
    return apiFetch("/api/ai/consent", {
      method: "POST",
      body: JSON.stringify({
        terms_version,
        data_policy_version,
        privacy_policy_version
      })
    });
  },

  // GET /api/ai/consent/users — admin consent list
  async loadConsentUsers({ page = 1, pageSize = 20, search = "", status = "" } = {}) {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize)
    });
    if (search.trim()) params.set("search", search.trim());
    if (status.trim()) params.set("status", status.trim());
    return apiFetch(`/api/ai/consent/users?${params.toString()}`);
  },

  // GET /api/ai/consent/users/:userId — admin user consent detail
  async loadConsentUser(userId) {
    return apiFetch(`/api/ai/consent/users/${userId}`);
  },

  // GET /api/ai/consent/users/:userId/history — admin acceptance history
  async loadConsentUserHistory(userId, { page = 1, pageSize = 20 } = {}) {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize)
    });
    return apiFetch(`/api/ai/consent/users/${userId}/history?${params.toString()}`);
  },

  async loadTokenUsage() {
    return apiFetch("/api/token-usage");
  },

  async loadLessorTokenUsage() {
    const response = await apiFetch("/api/token-access/");
    return normalizeAllUsersTokenRequests(response);
  },

  async loadTokenRechargePackages() {
    const response = await apiFetch("/api/token-recharge-packages");
    return response.results || response || [];
  },

  async loadTokenRechargeRequests({ page = 1, pageSize = 10 } = {}) {
    return apiFetch(
      `/api/token-recharge-requests?page=${page}&page_size=${pageSize}`
    );
  },

  async createTokenRechargeRequest(tokenAmount, requestComments) {
    const trimmedRequestComments = String(requestComments || "").trim();
    if (trimmedRequestComments.length > 1000) {
      throw new Error("Request comments cannot exceed 1000 characters.");
    }

    return apiFetch("/api/token-recharge-requests", {
      method: "POST",
      body: JSON.stringify({
        token_amount: tokenAmount,
        request_comments: trimmedRequestComments
      })
    });
  },

  async cancelTokenRechargeRequest(requestId, comments) {
    return apiFetch(`/api/token-recharge-requests/${requestId}/cancel`, {
      method: "POST",
      body: JSON.stringify({ comments })
    });
  },

  async loadAllUsersTokenRechargeRequests({
    page = 1,
    page_size = 10,
    ...filters
  } = {}) {
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(
        ([, value]) => value !== null && value !== undefined && value !== ""
      )
    );
    const query = {
      page,
      page_size,
      ...cleanFilters
    };

    const params = new URLSearchParams(
      Object.entries(query).reduce((acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      }, {})
    );
    const response = await apiFetch(
      `/api/token-recharge-requests/all-users?${params.toString()}`
    );
    return normalizeAllUsersTokenRequests(response, page, page_size);
  },

  async reviewTokenRechargeRequest(requestId, { status, comments } = {}) {
    const normalizedStatus = String(status || "").trim();
    const trimmedComments = String(comments || "").trim();

    if (!["approved", "rejected"].includes(normalizedStatus)) {
      throw new Error("Status must be approved or rejected.");
    }
    if (trimmedComments.length > 1000) {
      throw new Error("Comments cannot exceed 1000 characters.");
    }

    return apiFetch(`/api/token-recharge-requests/${requestId}/`, {
      method: "PUT",
      body: JSON.stringify({
        status: normalizedStatus,
        comments: trimmedComments
      })
    });
  },

  async loadThreads({ page = 1, pageSize = 20, search = "" } = {}) {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize)
    });
    if (search.trim()) params.set("search", search.trim());
    return apiFetch(`/api/threads?${params.toString()}`);
  },

  async loadThread(threadId) {
    return apiFetch(`/api/threads/${threadId}`);
  },

  async loadOverallTokens() {
    return apiFetch("/api/overall-tokens");
  },

  async createThread(initialQuestion = "") {
    return apiFetch("/api/threads", {
      method: "POST",
      body: JSON.stringify({ title: createTitle(initialQuestion) })
    });
  },

  async askQuestion({ threadId, question, memoryEnabled = true }) {
    return apiFetch(`/api/threads/${threadId}/questions`, {
      method: "POST",
      body: JSON.stringify({
        question,
        lessor_id: lessorId?.id,
        memory_enabled: memoryEnabled
      })
    });
  },

  async submitFeedback({ threadId, messageId, rating, comments = "" }) {
    return apiFetch(`/api/threads/${threadId}/messages/${messageId}/feedback`, {
      method: "POST",
      body: JSON.stringify({ rating, comments, lessor_id: lessorId?.id })
    });
  },

  async loadFewShots({ search = "", limit = 8 } = {}) {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    params.set("limit", String(limit));
    return apiFetch(`/api/few-shots?${params.toString()}`);
  },

  async loadSavedQuestions() {
    return apiFetch("/api/saved-questions");
  }
};
